import { useState, useEffect } from "react";
import { X, Download, Smartphone, Share, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { usePWA } from "@/hooks/usePWA";

export function InstallPrompt() {
  const { isInstallable, isInstalled, isIOS, isStandalone, install } = usePWA();
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if prompt was dismissed recently (within 7 days)
    const dismissedAt = localStorage.getItem("pwa_prompt_dismissed");
    if (dismissedAt) {
      const dismissedDate = new Date(dismissedAt);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        setDismissed(true);
        return;
      }
    }

    // Show prompt after a short delay for better UX
    const timer = setTimeout(() => {
      if ((isInstallable || isIOS) && !isInstalled && !isStandalone) {
        setShowPrompt(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [isInstallable, isInstalled, isIOS, isStandalone]);

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem("pwa_prompt_dismissed", new Date().toISOString());
  };

  const handleInstall = async () => {
    const success = await install();
    if (success) {
      setShowPrompt(false);
    }
  };

  if (!showPrompt || dismissed || isInstalled || isStandalone) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm animate-in slide-in-from-bottom-4 duration-300">
      <Card className="border-primary/20 bg-card shadow-xl">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Smartphone className="h-5 w-5 text-primary" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-foreground text-sm">
                    Instalar Sentinela
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Acesse rápido direto da sua tela inicial
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Fechar prompt de instalação"
                  className="h-6 w-6 shrink-0 -mt-1 -mr-1"
                  onClick={handleDismiss}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>

              {isIOS ? (
                <div className="mt-3 p-2 rounded-md bg-muted/50 text-xs text-muted-foreground">
                  <p className="flex items-center gap-1.5">
                    <Share className="h-3.5 w-3.5" />
                    Toque em <strong>Compartilhar</strong>
                  </p>
                  <p className="flex items-center gap-1.5 mt-1">
                    <Plus className="h-3.5 w-3.5" />
                    Depois em <strong>Adicionar à Tela Inicial</strong>
                  </p>
                </div>
              ) : (
                <Button
                  size="sm"
                  className="mt-3 w-full"
                  onClick={handleInstall}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Instalar App
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
